/**
 * Mirror block (spec §7.4.13, §8.18): shows another block with its subtree, or every block of another
 * page, live. It opens the original's page document and draws its blocks with `BlockTree` in embedded
 * mode under that page's own editor context, so every edit — and undo — acts on the original.
 */
import { Link } from "@tanstack/react-router";
import type { MirrorSource } from "@kss/schema";
import { CursorEphemeralStore } from "loro-prosemirror";
import { UndoManager, type LoroDoc, type TreeID } from "loro-crdt";
import { Layers } from "lucide-react";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { usePage } from "../../api/hooks";
import { PageEditorContext, usePageEditorContext, type FocusTarget, type PageEditorContextValue } from "../../pages/pageEditorContext";
import { useDoc, useJoinSettled, useLoroSelector, useSyncStatus } from "../../sync/hooks";
import { parseSource } from "../clipboard/sources";
import { BlockTree } from "../BlockTree";
import { blocksTree, pageTitleText } from "../model";
import type { BlockComponentProps } from "../registry";
import { bodyMap } from "../textField";
import { useT } from "../../i18n";

const MAX_DEPTH = 4;
/** sources being rendered above this point: `docId` for page mirrors, `docId:treeId` for block mirrors */
const MirrorAncestry = createContext<string[]>([]);
const NO_FOCUS = new Map<string, Array<{ name: string; color: string }>>();

/** One editor context per source document, shared by every mirror of it (one undo history). */
const contexts = new WeakMap<LoroDoc, PageEditorContextValue>();
function editorContextFor(doc: LoroDoc): PageEditorContextValue {
  let ctx = contexts.get(doc);
  if (!ctx) {
    const views = new Map<TreeID, FocusTarget>();
    ctx = {
      undoManager: new UndoManager(doc, { excludeOriginPrefixes: ["sys:"], mergeInterval: 500 }),
      cursors: new CursorEphemeralStore(doc.peerIdStr),
      registerView: (id, v) => views.set(id, v),
      unregisterView: (id) => views.delete(id),
      focusBlock: (id, edge) => views.get(id)?.focusEdge(edge),
    };
    contexts.set(doc, ctx);
  }
  return ctx;
}

function Placeholder({ children, testState }: { children: ReactNode; testState: string }) {
  return (
    <div className="px-3 py-2 text-sm text-neutral-500" data-testid="mirror-placeholder" data-state={testState}>
      {children}
    </div>
  );
}

export function MirrorBlock({ handle, treeId, node, readOnly, userId, workspaceId }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const source = useLoroSelector(handle.doc, () => (body?.get("source") as MirrorSource | null | undefined) ?? null, (a, b) => a?.iri === b?.iri);
  const parsed = source ? parseSource(source) : null;
  const ancestry = useContext(MirrorAncestry);
  const key = parsed ? (parsed.treeId ? `${parsed.docId}:${parsed.treeId}` : parsed.docId) : "";
  const loop = !!parsed && (ancestry.includes(key) || ancestry.includes(parsed.docId) || (!parsed.treeId && parsed.docId === handle.crdtDocId));

  return (
    <div className="rounded border border-dashed border-neutral-300 dark:border-neutral-700" data-testid="mirror-block" data-source={source?.iri ?? ""}>
      {!parsed ? (
        <Placeholder testState="invalid">{t("mirror.noOriginal")}</Placeholder>
      ) : loop ? (
        <Placeholder testState="loop">{t("mirror.loop")}</Placeholder>
      ) : ancestry.length >= MAX_DEPTH ? (
        <Placeholder testState="depth">{t("mirror.tooDeep")}</Placeholder>
      ) : (
        <MirrorContent hostHandle={handle} hostTreeId={treeId} docId={parsed.docId} rootTreeId={parsed.treeId} readOnly={readOnly} userId={userId} workspaceId={workspaceId} ancestry={[...ancestry, key]} />
      )}
    </div>
  );
}

function MirrorContent({ hostHandle, docId, rootTreeId, userId, workspaceId, ancestry }: { hostHandle: BlockComponentProps["handle"]; hostTreeId: TreeID; docId: string; rootTreeId?: TreeID; readOnly: boolean; userId: string; workspaceId?: string; ancestry: string[] }) {
  const t = useT();
  const src = useDoc("page", docId);
  const page = usePage(docId);
  const settled = useJoinSettled(src);
  const status = useSyncStatus(src);
  const hostCtx = usePageEditorContext();
  const ctx = useMemo(() => (src ? (src.doc === hostHandle.doc ? hostCtx : editorContextFor(src.doc)) : null), [src, hostHandle.doc, hostCtx]);
  const state = useLoroSelector(
    src?.doc ?? null,
    (d) => {
      const tree = blocksTree(d);
      const title = pageTitleText(d)?.toString() ?? "";
      const known = d.getMap("meta").get("schemaVersion") != null || tree.roots().length > 0;
      if (!rootTreeId) return { known, exists: known, title };
      const node = tree.getNodeByID(rootTreeId);
      let exists = false;
      try {
        exists = !!node && !tree.isNodeDeleted(rootTreeId);
      } catch {
        exists = false;
      }
      return { known, exists, title };
    },
    (a, b) => a.known === b.known && a.exists === b.exists && a.title === b.title,
  );

  if (!src || !ctx || !state) return <Placeholder testState="loading">{t("common.loading")}</Placeholder>;
  const title = state.title || page.data?.title || t("page.titlePlaceholder");
  if (page.data && workspaceId && page.data.workspace_id !== workspaceId) return <Placeholder testState="workspace">{t("mirror.otherWorkspace")}</Placeholder>;
  if (!state.known) {
    if (!settled) return <Placeholder testState="loading">{t("common.loading")}</Placeholder>;
    if (status === "offline") return <Placeholder testState="offline">{t("mirror.offline")}</Placeholder>;
    if (status === "forbidden") return <Placeholder testState="forbidden">{t("mirror.forbidden")}</Placeholder>;
  }
  if ((page.isError && settled) || (settled && !state.exists)) return <Placeholder testState="deleted">{t("mirror.deleted")}</Placeholder>;

  const srcReadOnly = src.mode === "read" || page.data?.my_mode === "read";
  return (
    <div
      onKeyDown={(e) => {
        // undo inside a mirror is undo in the original (its page's history), never the host page's
        const mod = e.metaKey || e.ctrlKey;
        if (!mod || !["z", "y"].includes(e.key.toLowerCase())) return;
        e.stopPropagation();
        if (e.defaultPrevented) return; // an editor already undid through the same manager
        e.preventDefault();
        if (e.key.toLowerCase() === "z" && !e.shiftKey) ctx.undoManager.undo();
        else ctx.undoManager.redo();
      }}
    >
      <div className="flex items-center gap-1 border-b border-dashed border-neutral-300 px-2 py-0.5 text-xs text-neutral-500 dark:border-neutral-700" data-testid="mirror-header">
        <Layers size={12} />
        <span>{t("mirror.of")}</span>
        {workspaceId ? (
          <Link to="/w/$workspaceId/d/$docId" params={{ workspaceId, docId }} hash={rootTreeId ? `block=${encodeURIComponent(rootTreeId)}` : undefined} className="truncate text-accent underline" data-testid="mirror-original-link">
            {rootTreeId ? t("mirror.blockOn", { title }) : t("mirror.page", { title })}
          </Link>
        ) : (
          <span className="truncate">{title}</span>
        )}
        {srcReadOnly && <span className="ml-auto rounded bg-neutral-200 px-1 dark:bg-neutral-800">{t("ws.readOnly")}</span>}
      </div>
      <PageEditorContext.Provider value={ctx}>
        <MirrorAncestry.Provider value={ancestry}>
          <BlockTree handle={src} readOnly={srcReadOnly} userId={userId} focusedBy={NO_FOCUS} onFocusBlock={() => {}} workspaceId={workspaceId} embedded={{ rootTreeId, sourceLabel: title }} />
        </MirrorAncestry.Provider>
      </PageEditorContext.Provider>
    </div>
  );
}
